import { useMutation } from '@apollo/client';
import { MutableRefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import type { ContractTemplate, ContractTemplateAssignment } from 'global-intern-types';
import { CREATE_CONTRACT_TEMPLATE, UPDATE_CONTRACT_TEMPLATE } from '../api/contractTemplates';
import type { VertragEditorApi } from '../contractEditor/editor/useVertragEditor';
import {
  DOKUMENT_MAX_BYTES,
  hatSichtbarenText,
  speicherdokument,
  speicherText,
  utf8Bytes,
} from '../contractEditor/model/speicherform';
import {
  graphqlFehlerCode,
  graphqlFehlerErweiterungen,
  speicherfehlerZuordnen,
  VERTRAG_INHALT_FEHLT_TEXT,
  VERTRAG_ZU_GROSS_TEXT,
} from '../services/vertragsvorlageFehler';

import { speichernGesperrtText } from '../services/vorlagensperreTexte';
import {
  pruefeZuordnung,
  zuordnungZumFormular,
  zuordnungZumInput,
  VorlagenZuordnungForm,
  Zuordnungsfehler,
} from '../services/vorlagenZuordnung';

export interface VertragsvorlageStart {
  id: number | null;
  name: string;
  beschreibung: string;
  assignment?: ContractTemplateAssignment | null;
  /** RITA-1541: `updated_at` beim Öffnen – Vergleichswert für die Warnung beim Speichern. */
  updatedAt?: string | null;
  /** Eine Kopie ist bereits vor der ersten Eingabe ein ungespeicherter Entwurf. */
  kopie?: boolean;
}

interface Eigenschaften {
  start: VertragsvorlageStart;
  editor: VertragEditorApi;
  onAngelegt: (id: number) => void;
  freigabeRef: MutableRefObject<() => void>;
  /** RITA-1541: Kennung dieses Fensters; erfolgreiches Speichern beendet dessen Sperre. */
  fensterkennung?: () => string | undefined;
  /** RITA-1541: false → nicht speichern, weil jemand anderes bearbeitet; synchron true, wenn keine Nachfrage nötig ist. */
  vorSpeichern?: () => boolean | Promise<boolean>;
  onGespeichert?: () => void;
}

export interface Speicheroptionen {
  /** „Trotzdem speichern“: ohne Vergleichswert bewusst überschreiben. */
  ueberschreiben?: boolean;
}

interface Vergleichsstand {
  name: string;
  beschreibung: string;
  dokumentText: string;
  zuordnungText: string;
}

type Speicherantwort = Pick<ContractTemplate, 'id'> & { updated_at?: string | null };

export default function useVertragsvorlage({
  start,
  editor,
  onAngelegt,
  freigabeRef,
  fensterkennung,
  vorSpeichern,
  onGespeichert,
}: Eigenschaften) {
  const [name, setNameWert] = useState(start.name);
  const [beschreibung, setBeschreibungWert] = useState(start.beschreibung);
  const [zuordnung, setZuordnungWert] = useState(() => zuordnungZumFormular(start.assignment));
  const [zuordnungsfehler, setZuordnungsfehler] = useState<Zuordnungsfehler>({});
  const [vorlagenId, setVorlagenId] = useState(start.id);
  const [nameFehler, setNameFehler] = useState('');
  const [beschreibungFehler, setBeschreibungFehler] = useState('');
  const [hinweisOben, setHinweisOben] = useState('');
  const [hatZuordnungskonflikt, setHatZuordnungskonflikt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  // RITA-1541: gespeicherter Stand (updated_at des Servers) und offener Speicherkonflikt (Zeit der fremden Speicherung).
  const [gespeicherterStand, setGespeicherterStand] = useState<string | null>(start.updatedAt ?? null);
  const [konflikt, setKonflikt] = useState<string | null>(null);
  const speichertRef = useRef(false);
  const aktivRef = useRef(true);
  const [anlegen] = useMutation<{ createContractTemplate: Speicherantwort }>(CREATE_CONTRACT_TEMPLATE);
  const [aendern] = useMutation<{ updateContractTemplate: Speicherantwort }>(UPDATE_CONTRACT_TEMPLATE);
  const { dokument, komposition } = editor.zustand;
  const dokumentText = useMemo(
    () => speicherText(speicherdokument({ dokument, komposition })),
    [dokument, komposition],
  );
  const [vergleich, setVergleich] = useState<Vergleichsstand>(() => ({
    name: start.name,
    beschreibung: start.beschreibung,
    dokumentText,
    zuordnungText: JSON.stringify(zuordnungZumFormular(start.assignment)),
  }));

  useLayoutEffect(() => {
    aktivRef.current = true;
    return () => {
      aktivRef.current = false;
    };
  }, []);

  function setName(wert: string): void {
    setNameWert(wert);
    setNameFehler('');
  }

  function setBeschreibung(wert: string): void {
    setBeschreibungWert(wert);
    setBeschreibungFehler('');
  }

  function setZuordnung(wert: VorlagenZuordnungForm): void {
    if (speichertRef.current) return;
    setZuordnungWert(wert);
    setZuordnungsfehler({});
    if (hatZuordnungskonflikt) {
      setHinweisOben('');
      setHatZuordnungskonflikt(false);
    }
  }

  /** Der Stand, der gespeichert würde, und seine Eingabefehler (ohne Zustand zu setzen). */
  function eingabenPruefen() {
    const gesendet = {
      name: name.trim(),
      beschreibung: beschreibung.trim(),
      dokument: speicherdokument(editor.zustand),
      zuordnung,
    };
    const gesendeterText = speicherText(gesendet.dokument);
    let namensfehler = '';
    if (!gesendet.name) namensfehler = 'Bitte einen Namen eingeben';
    else if (gesendet.name.length > 120) namensfehler = 'Bitte einen Namen mit höchstens 120 Zeichen eingeben';
    const beschreibungsfehler =
      gesendet.beschreibung.length > 250 ? 'Die interne Beschreibung darf höchstens 250 Zeichen lang sein' : '';
    let dokumentfehler = '';
    if (!hatSichtbarenText(gesendet.dokument)) dokumentfehler = VERTRAG_INHALT_FEHLT_TEXT;
    else if (utf8Bytes(gesendeterText) > DOKUMENT_MAX_BYTES) dokumentfehler = VERTRAG_ZU_GROSS_TEXT;
    const zuordnungFehler = pruefeZuordnung(gesendet.zuordnung);
    return { gesendet, gesendeterText, namensfehler, beschreibungsfehler, dokumentfehler, zuordnungFehler };
  }

  /**
   * RITA-1599: Würde Speichern die Eingaben annehmen? Nur dann fragt die Seite wegen der Hinweise nach; fehlt etwa der
   * Name, zeigt Speichern wie bisher zuerst diesen Fehler.
   */
  function eingabenGueltig(): boolean {
    const { namensfehler, beschreibungsfehler, dokumentfehler, zuordnungFehler } = eingabenPruefen();
    return !namensfehler && !beschreibungsfehler && !dokumentfehler && Object.keys(zuordnungFehler).length === 0;
  }

  async function speichern(optionen: Speicheroptionen = {}): Promise<void> {
    if (speichertRef.current || !aktivRef.current) return;
    const { gesendet, gesendeterText, namensfehler, beschreibungsfehler, dokumentfehler, zuordnungFehler } =
      eingabenPruefen();
    setZuordnungsfehler(zuordnungFehler);
    setNameFehler(namensfehler);
    setBeschreibungFehler(beschreibungsfehler);
    setHinweisOben(dokumentfehler);
    setHatZuordnungskonflikt(false);
    if (namensfehler || beschreibungsfehler || dokumentfehler || Object.keys(zuordnungFehler).length > 0) return;

    speichertRef.current = true;
    setSpeichert(true);
    const input = {
      name: gesendet.name,
      description: gesendet.beschreibung || null,
      document: gesendet.dokument,
      assignment: zuordnungZumInput(gesendet.zuordnung),
    };
    const istNeu = vorlagenId === null;
    try {
      // RITA-1541: Bearbeitet inzwischen jemand anderes, wird nicht gespeichert; das Band erklärt es.
      if (!istNeu && vorSpeichern) {
        const erlaubt = vorSpeichern();
        if (erlaubt !== true && !(await erlaubt)) {
          if (aktivRef.current) setSpeichert(false);
          return;
        }
      }
      const variablen = {
        input,
        id: vorlagenId,
        expectedUpdatedAt: optionen.ueberschreiben ? null : (gespeicherterStand ?? undefined),
        lockToken: fensterkennung?.(),
      };
      const antwort = istNeu
        ? (await anlegen({ variables: { input } })).data?.createContractTemplate
        : (await aendern({ variables: variablen })).data?.updateContractTemplate;
      if (!aktivRef.current) return;
      if (!antwort || !Number.isInteger(antwort.id) || antwort.id <= 0 || (!istNeu && antwort.id !== vorlagenId)) {
        throw new Error('Ungueltige Speicherantwort');
      }
      // React 17 bündelt diese Änderungen nach await nicht selbst. Der Wächter sieht nur den fertigen Stand.
      unstable_batchedUpdates(() => {
        setVergleich({
          name: gesendet.name,
          beschreibung: gesendet.beschreibung,
          dokumentText: gesendeterText,
          zuordnungText: JSON.stringify(gesendet.zuordnung),
        });
        setNameWert(gesendet.name);
        setBeschreibungWert(gesendet.beschreibung);
        setVorlagenId(antwort.id);
        setGespeicherterStand(typeof antwort.updated_at === 'string' ? antwort.updated_at : null);
        setKonflikt(null);
        setSpeichert(false);
      });
      onGespeichert?.();
      if (istNeu) {
        freigabeRef.current();
        onAngelegt(antwort.id);
      }
    } catch (fehler) {
      if (!aktivRef.current) return;
      if (graphqlFehlerCode(fehler) === 'CONFLICT') {
        // RITA-1541: nie still überschreiben – das Fenster nennt die Uhrzeit der fremden Speicherung.
        const { updated_at: fremdGespeichert } = graphqlFehlerErweiterungen(fehler);
        unstable_batchedUpdates(() => {
          setKonflikt(typeof fremdGespeichert === 'string' ? fremdGespeichert : new Date().toISOString());
          setSpeichert(false);
        });
        return;
      }
      if (graphqlFehlerCode(fehler) === 'LOCKED') {
        // RITA-1541: Jemand anderes hält die Sperre (z. B. nach eigenem Ablauf). Das Band folgt beim nächsten Nachsehen.
        const { display_name: halterName } = graphqlFehlerErweiterungen(fehler);
        unstable_batchedUpdates(() => {
          setHinweisOben(speichernGesperrtText(typeof halterName === 'string' ? halterName : 'jemand anderem'));
          // Kam LOCKED auf „Trotzdem speichern“, schließt das Konfliktfenster, damit der Hinweis sichtbar ist.
          setKonflikt(null);
          setSpeichert(false);
        });
        return;
      }
      const speicherfehler = speicherfehlerZuordnen(fehler);
      unstable_batchedUpdates(() => {
        // Nach „Trotzdem speichern“ muss die abgelehnte Zuordnung im erhaltenen Entwurf korrigierbar sein.
        if (graphqlFehlerCode(fehler) === 'ASSIGNMENT_CONFLICT') {
          setKonflikt(null);
          setHatZuordnungskonflikt(true);
        }
        if (speicherfehler.ziel === 'name') setNameFehler(speicherfehler.text);
        else if (speicherfehler.ziel === 'beschreibung') setBeschreibungFehler(speicherfehler.text);
        else setHinweisOben(speicherfehler.text);
        setSpeichert(false);
      });
    } finally {
      speichertRef.current = false;
    }
  }

  return {
    name,
    setName,
    beschreibung,
    setBeschreibung,
    zuordnung,
    setZuordnung,
    zuordnungsfehler,
    nameFehler,
    beschreibungFehler,
    hinweisOben,
    geaendert:
      (start.kopie === true && vorlagenId === null) ||
      name !== vergleich.name ||
      beschreibung !== vergleich.beschreibung ||
      dokumentText !== vergleich.dokumentText ||
      JSON.stringify(zuordnung) !== vergleich.zuordnungText,
    vorlagenId,
    speichert,
    speichern,
    eingabenGueltig,
    /** RITA-1541: Normalform des Dokuments; zusammen mit Name/Beschreibung/Zuordnung die Eingabespur. */
    dokumentText,
    konflikt,
    konfliktSchliessen: () => setKonflikt(null),
  };
}
